import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";

/**
 * Settings → Workflow → Diligence depth (SAAS-915).
 *
 * Chooses how deeply a *new* deal is analysed. Off is the behaviour every deal
 * has always had, so turning this on is an opt-in to spending less per deal,
 * never a silent downgrade of what a workspace already gets.
 *
 * Both tiers run the same four analyses; the light one runs cheaper variants
 * of market research and the website summary. That is the honest description
 * of the difference — it is depth per report, not fewer reports.
 */
export function DiligenceDepthSettings() {
  const { workspace, updateWorkspaceMutation } = useWorkspaceSettings();
  const [isSaving, setIsSaving] = useState(false);

  const isLight = workspace?.default_diligence_level === "light";

  const handleToggle = (light: boolean) => {
    setIsSaving(true);
    updateWorkspaceMutation.mutate(
      { default_diligence_level: light ? "light" : "full" },
      {
        onSuccess: () => {
          setIsSaving(false);
          toast.success(
            light
              ? "New deals will start with a light screen"
              : "New deals will get full diligence"
          );
        },
        onError: () => setIsSaving(false),
      }
    );
  };

  return (
    <section>
      <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
        Diligence Depth
      </h2>
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-1">
            <Label htmlFor="light-screen-toggle">
              Screen new deals lightly
            </Label>
            <p className="text-sm text-muted-foreground">
              New deals run a light screen instead of full diligence. Market
              research and the website summary run in brief; deal extraction
              and thesis-fit scoring are unchanged, so every deal is still
              scored and rankable.
            </p>
            <p className="text-sm text-muted-foreground">
              Any deal can be taken to full diligence later from its Reports
              tab, which regenerates both in depth.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 pt-1">
            {isSaving && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <Switch
              id="light-screen-toggle"
              checked={isLight}
              disabled={isSaving}
              onCheckedChange={handleToggle}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
